/**
 * Custom Phone Input Component
 * 
 * A custom phone input that displays country code inside the input field
 * and uses individual flag CSS classes for easy styling
 * 
 * @package A2A_Theme
 */

class CustomPhoneInput {
    constructor(inputElement, options = {}) {
        this.input = inputElement;
        this.options = {
            initialCountry: 'gb',
            preferredCountries: ['gb', 'us', 'fr', 'de', 'it', 'es', 'ch', 'at'],
            ...options
        };
        
        this.countries = this.getCountriesData();
        this.selectedCountry = null; // No default selection
        this.isSettingCountryProgrammatically = false; // Flag to prevent auto-detection during programmatic changes
        
        this.init();
    }
    
    getCountriesData() {
        return [
            { code: 'ad', name: 'Andorra', dialCode: '+376', country_code_id_1: 33 },
            { code: 'ae', name: 'United Arab Emirates', dialCode: '+971', country_code_id_1: 181 },
            { code: 'af', name: 'Afghanistan', dialCode: '+93', country_code_id_1: 37 },
            { code: 'ag', name: 'Antigua and Barbuda', dialCode: '+1268', country_code_id_1: 30 },
            { code: 'ai', name: 'Anguilla', dialCode: '+1264', country_code_id_1: 31 },
            { code: 'al', name: 'Albania', dialCode: '+355', country_code_id_1: 36 },
            { code: 'am', name: 'Armenia', dialCode: '+374', country_code_id_1: 28 },
            { code: 'ao', name: 'Angola', dialCode: '+244', country_code_id_1: 32 },
            { code: 'ar', name: 'Argentina', dialCode: '+54', country_code_id_1: 29 },
            { code: 'as', name: 'American Samoa', dialCode: '+1684', country_code_id_1: 34 },
            { code: 'at', name: 'Austria', dialCode: '+43', country_code_id_1: 25 },
            { code: 'au', name: 'Australia', dialCode: '+61', country_code_id_1: 26 },
            { code: 'aw', name: 'Aruba', dialCode: '+297', country_code_id_1: 27 },
            { code: 'az', name: 'Azerbaijan', dialCode: '+994', country_code_id_1: 24 },
            { code: 'ba', name: 'Bosnia and Herzegovina', dialCode: '+387', country_code_id_1: 13 },
            { code: 'bb', name: 'Barbados', dialCode: '+1246', country_code_id_1: 20 },
            { code: 'bd', name: 'Bangladesh', dialCode: '+880', country_code_id_1: 22 },
            { code: 'be', name: 'Belgium', dialCode: '+32', country_code_id_1: 18 },
            { code: 'bf', name: 'Burkina Faso', dialCode: '+226', country_code_id_1: 8 },
            { code: 'bg', name: 'Bulgaria', dialCode: '+359', country_code_id_1: 9 },
            { code: 'bh', name: 'Bahrain', dialCode: '+973', country_code_id_1: 21 },
            { code: 'bi', name: 'Burundi', dialCode: '+257', country_code_id_1: 5 },
            { code: 'bj', name: 'Benin', dialCode: '+229', country_code_id_1: 16 },
            { code: 'bn', name: 'Brunei', dialCode: '+673', country_code_id_1: 10 },
            { code: 'bo', name: 'Bolivia', dialCode: '+591', country_code_id_1: 14 },
            { code: 'br', name: 'Brazil', dialCode: '+55', country_code_id_1: 11 },
            { code: 'bs', name: 'Bahamas', dialCode: '+1242', country_code_id_1: 23 },
            { code: 'bt', name: 'Bhutan', dialCode: '+975', country_code_id_1: 15 },
            { code: 'bw', name: 'Botswana', dialCode: '+267', country_code_id_1: 12 },
            { code: 'by', name: 'Belarus', dialCode: '+375', country_code_id_1: 19 },
            { code: 'bz', name: 'Belize', dialCode: '+501', country_code_id_1: 17 },
            { code: 'ca', name: 'Canada', dialCode: '+1', country_code_id_1: 4 },
            { code: 'cd', name: 'DR Congo', dialCode: '+243', country_code_id_1: 42 },
            { code: 'cf', name: 'Central African Republic', dialCode: '+236', country_code_id_1: 2 },
            { code: 'cg', name: 'Republic of the Congo', dialCode: '+242', country_code_id_1: 41 },
            { code: 'ch', name: 'Switzerland', dialCode: '+41', country_code_id_1: 169 },
            { code: 'ci', name: 'Côte d\'Ivoire', dialCode: '+225', country_code_id_1: 89 },
            { code: 'cl', name: 'Chile', dialCode: '+56', country_code_id_1: 1 },
            { code: 'cm', name: 'Cameroon', dialCode: '+237', country_code_id_1: 7 },
            { code: 'cn', name: 'China', dialCode: '+86', country_code_id_1: 38 },
            { code: 'co', name: 'Colombia', dialCode: '+57', country_code_id_1: 39 },
            { code: 'cr', name: 'Costa Rica', dialCode: '+506', country_code_id_1: 43 },
            { code: 'cu', name: 'Cuba', dialCode: '+53', country_code_id_1: 45 },
            { code: 'cv', name: 'Cape Verde', dialCode: '+238', country_code_id_1: 3 },
            { code: 'cy', name: 'Cyprus', dialCode: '+357', country_code_id_1: 46 },
            { code: 'cz', name: 'Czech Republic', dialCode: '+420', country_code_id_1: 47 },
            { code: 'de', name: 'Germany', dialCode: '+49', country_code_id_1: 67 },
            { code: 'dj', name: 'Djibouti', dialCode: '+253', country_code_id_1: 49 },
            { code: 'dk', name: 'Denmark', dialCode: '+45', country_code_id_1: 48 },
            { code: 'dm', name: 'Dominica', dialCode: '+1767', country_code_id_1: 50 },
            { code: 'do', name: 'Dominican Republic', dialCode: '+1', country_code_id_1: 51 },
            { code: 'dz', name: 'Algeria', dialCode: '+213', country_code_id_1: 35 },
            { code: 'ec', name: 'Ecuador', dialCode: '+593', country_code_id_1: 53 },
            { code: 'ee', name: 'Estonia', dialCode: '+372', country_code_id_1: 58 },
            { code: 'eg', name: 'Egypt', dialCode: '+20', country_code_id_1: 54 },
            { code: 'er', name: 'Eritrea', dialCode: '+291', country_code_id_1: 57 },
            { code: 'es', name: 'Spain', dialCode: '+34', country_code_id_1: 163 },
            { code: 'et', name: 'Ethiopia', dialCode: '+251', country_code_id_1: 59 },
            { code: 'fi', name: 'Finland', dialCode: '+358', country_code_id_1: 61 },
            { code: 'fj', name: 'Fiji', dialCode: '+679', country_code_id_1: 60 },
            { code: 'fr', name: 'France', dialCode: '+33', country_code_id_1: 62 },
            { code: 'gb', name: 'United Kingdom', dialCode: '+44', country_code_id_1: 182 },
            { code: 'gd', name: 'Grenada', dialCode: '+1473', country_code_id_1: 71 },
            { code: 'ge', name: 'Georgia', dialCode: '+995', country_code_id_1: 66 },
            { code: 'gf', name: 'French Guiana', dialCode: '+594', country_code_id_1: 64 },
            { code: 'gh', name: 'Ghana', dialCode: '+233', country_code_id_1: 68 },
            { code: 'gi', name: 'Gibraltar', dialCode: '+350', country_code_id_1: 69 },
            { code: 'gm', name: 'Gambia', dialCode: '+220', country_code_id_1: 65 },
            { code: 'gn', name: 'Guinea', dialCode: '+224', country_code_id_1: 74 },
            { code: 'gp', name: 'Guadeloupe', dialCode: '+590', country_code_id_1: 73 },
            { code: 'gq', name: 'Equatorial Guinea', dialCode: '+240', country_code_id_1: 56 },
            { code: 'gr', name: 'Greece', dialCode: '+30', country_code_id_1: 70 },
            { code: 'gt', name: 'Guatemala', dialCode: '+502', country_code_id_1: 72 },
            { code: 'gw', name: 'Guinea-Bissau', dialCode: '+245', country_code_id_1: 75 },
            { code: 'gy', name: 'Guyana', dialCode: '+592', country_code_id_1: 76 },
            { code: 'hk', name: 'Hong Kong', dialCode: '+852', country_code_id_1: 79 },
            { code: 'hn', name: 'Honduras', dialCode: '+504', country_code_id_1: 78 },
            { code: 'hr', name: 'Croatia', dialCode: '+385', country_code_id_1: 44 },
            { code: 'ht', name: 'Haiti', dialCode: '+509', country_code_id_1: 77 },
            { code: 'hu', name: 'Hungary', dialCode: '+36', country_code_id_1: 80 },
            { code: 'id', name: 'Indonesia', dialCode: '+62', country_code_id_1: 83 },
            { code: 'ie', name: 'Ireland', dialCode: '+353', country_code_id_1: 86 },
            { code: 'il', name: 'Israel', dialCode: '+972', country_code_id_1: 87 },
            { code: 'in', name: 'India', dialCode: '+91', country_code_id_1: 82 },
            { code: 'iq', name: 'Iraq', dialCode: '+964', country_code_id_1: 85 },
            { code: 'ir', name: 'Iran', dialCode: '+98', country_code_id_1: 84 },
            { code: 'is', name: 'Iceland', dialCode: '+354', country_code_id_1: 81 },
            { code: 'it', name: 'Italy', dialCode: '+39', country_code_id_1: 88 },
            { code: 'jm', name: 'Jamaica', dialCode: '+1876', country_code_id_1: 90 },
            { code: 'jo', name: 'Jordan', dialCode: '+962', country_code_id_1: 92 },
            { code: 'jp', name: 'Japan', dialCode: '+81', country_code_id_1: 91 },
            { code: 'ke', name: 'Kenya', dialCode: '+254', country_code_id_1: 94 },
            { code: 'kg', name: 'Kyrgyzstan', dialCode: '+996', country_code_id_1: 98 },
            { code: 'kh', name: 'Cambodia', dialCode: '+855', country_code_id_1: 6 },
            { code: 'km', name: 'Comoros', dialCode: '+269', country_code_id_1: 40 },
            { code: 'kp', name: 'North Korea', dialCode: '+850', country_code_id_1: 133 },
            { code: 'kr', name: 'South Korea', dialCode: '+82', country_code_id_1: 162 },
            { code: 'kw', name: 'Kuwait', dialCode: '+965', country_code_id_1: 97 },
            { code: 'kz', name: 'Kazakhstan', dialCode: '+7', country_code_id_1: 93 },
            { code: 'la', name: 'Laos', dialCode: '+856', country_code_id_1: 99 },
            { code: 'lb', name: 'Lebanon', dialCode: '+961', country_code_id_1: 101 },
            { code: 'li', name: 'Liechtenstein', dialCode: '+423', country_code_id_1: 104 },
            { code: 'lk', name: 'Sri Lanka', dialCode: '+94', country_code_id_1: 164 },
            { code: 'ls', name: 'Lesotho', dialCode: '+266', country_code_id_1: 102 },
            { code: 'lt', name: 'Lithuania', dialCode: '+370', country_code_id_1: 105 },
            { code: 'lu', name: 'Luxembourg', dialCode: '+352', country_code_id_1: 106 },
            { code: 'lv', name: 'Latvia', dialCode: '+371', country_code_id_1: 100 },
            { code: 'ly', name: 'Libya', dialCode: '+218', country_code_id_1: 103 },
            { code: 'ma', name: 'Morocco', dialCode: '+212', country_code_id_1: 123 },
            { code: 'mc', name: 'Monaco', dialCode: '+377', country_code_id_1: 120 },
            { code: 'md', name: 'Moldova', dialCode: '+373', country_code_id_1: 119 },
            { code: 'me', name: 'Montenegro', dialCode: '+382', country_code_id_1: 121 },
            { code: 'mg', name: 'Madagascar', dialCode: '+261', country_code_id_1: 109 },
            { code: 'mk', name: 'North Macedonia', dialCode: '+389', country_code_id_1: 108 },
            { code: 'ml', name: 'Mali', dialCode: '+223', country_code_id_1: 113 },
            { code: 'mm', name: 'Myanmar', dialCode: '+95', country_code_id_1: 125 },
            { code: 'mn', name: 'Mongolia', dialCode: '+976', country_code_id_1: 122 },
            { code: 'mo', name: 'Macao', dialCode: '+853', country_code_id_1: 107 },
            { code: 'mq', name: 'Martinique', dialCode: '+596', country_code_id_1: 115 },
            { code: 'mr', name: 'Mauritania', dialCode: '+222', country_code_id_1: 116 },
            { code: 'mt', name: 'Malta', dialCode: '+356', country_code_id_1: 114 },
            { code: 'mu', name: 'Mauritius', dialCode: '+230', country_code_id_1: 117 },
            { code: 'mv', name: 'Maldives', dialCode: '+960', country_code_id_1: 112 },
            { code: 'mw', name: 'Malawi', dialCode: '+265', country_code_id_1: 110 },
            { code: 'mx', name: 'Mexico', dialCode: '+52', country_code_id_1: 118 },
            { code: 'my', name: 'Malaysia', dialCode: '+60', country_code_id_1: 111 },
            { code: 'mz', name: 'Mozambique', dialCode: '+258', country_code_id_1: 124 },
            { code: 'na', name: 'Namibia', dialCode: '+264', country_code_id_1: 126 },
            { code: 'ne', name: 'Niger', dialCode: '+227', country_code_id_1: 192 },
            { code: 'ng', name: 'Nigeria', dialCode: '+234', country_code_id_1: 132 },
            { code: 'ni', name: 'Nicaragua', dialCode: '+505', country_code_id_1: 131 },
            { code: 'nl', name: 'Netherlands', dialCode: '+31', country_code_id_1: 129 },
            { code: 'no', name: 'Norway', dialCode: '+47', country_code_id_1: 134 },
            { code: 'np', name: 'Nepal', dialCode: '+977', country_code_id_1: 128 },
            { code: 'nr', name: 'Nauru', dialCode: '+674', country_code_id_1: 127 },
            { code: 'nz', name: 'New Zealand', dialCode: '+64', country_code_id_1: 130 },
            { code: 'om', name: 'Oman', dialCode: '+968', country_code_id_1: 135 },
            { code: 'pa', name: 'Panama', dialCode: '+507', country_code_id_1: 138 },
            { code: 'pe', name: 'Peru', dialCode: '+51', country_code_id_1: 141 },
            { code: 'pg', name: 'Papua New Guinea', dialCode: '+675', country_code_id_1: 139 },
            { code: 'ph', name: 'Philippines', dialCode: '+63', country_code_id_1: 142 },
            { code: 'pk', name: 'Pakistan', dialCode: '+92', country_code_id_1: 136 },
            { code: 'pl', name: 'Poland', dialCode: '+48', country_code_id_1: 143 },
            { code: 'pr', name: 'Puerto Rico', dialCode: '+1', country_code_id_1: 145 },
            { code: 'ps', name: 'Palestine', dialCode: '+970', country_code_id_1: 137 },
            { code: 'pt', name: 'Portugal', dialCode: '+351', country_code_id_1: 144 },
            { code: 'py', name: 'Paraguay', dialCode: '+595', country_code_id_1: 140 },
            { code: 'qa', name: 'Qatar', dialCode: '+974', country_code_id_1: 146 },
            { code: 're', name: 'Réunion', dialCode: '+262', country_code_id_1: 63 },
            { code: 'ro', name: 'Romania', dialCode: '+40', country_code_id_1: 147 },
            { code: 'rs', name: 'Serbia', dialCode: '+381', country_code_id_1: 155 },
            { code: 'ru', name: 'Russia', dialCode: '+7', country_code_id_1: 148 },
            { code: 'rw', name: 'Rwanda', dialCode: '+250', country_code_id_1: 149 },
            { code: 'sa', name: 'Saudi Arabia', dialCode: '+966', country_code_id_1: 153 },
            { code: 'sc', name: 'Seychelles', dialCode: '+248', country_code_id_1: 156 },
            { code: 'sd', name: 'Sudan', dialCode: '+249', country_code_id_1: 165 },
            { code: 'se', name: 'Sweden', dialCode: '+46', country_code_id_1: 168 },
            { code: 'sg', name: 'Singapore', dialCode: '+65', country_code_id_1: 158 },
            { code: 'si', name: 'Slovenia', dialCode: '+386', country_code_id_1: 160 },
            { code: 'sk', name: 'Slovakia', dialCode: '+421', country_code_id_1: 159 },
            { code: 'sl', name: 'Sierra Leone', dialCode: '+232', country_code_id_1: 157 },
            { code: 'sm', name: 'San Marino', dialCode: '+378', country_code_id_1: 151 },
            { code: 'sn', name: 'Senegal', dialCode: '+221', country_code_id_1: 154 },
            { code: 'sr', name: 'Suriname', dialCode: '+597', country_code_id_1: 166 },
            { code: 'st', name: 'São Tomé and Príncipe', dialCode: '+239', country_code_id_1: 152 },
            { code: 'sv', name: 'El Salvador', dialCode: '+503', country_code_id_1: 55 },
            { code: 'sy', name: 'Syria', dialCode: '+963', country_code_id_1: 170 },
            { code: 'sz', name: 'Eswatini', dialCode: '+268', country_code_id_1: 167 },
            { code: 'th', name: 'Thailand', dialCode: '+66', country_code_id_1: 174 },
            { code: 'tg', name: 'Togo', dialCode: '+228', country_code_id_1: 175 },
            { code: 'tj', name: 'Tajikistan', dialCode: '+992', country_code_id_1: 172 },
            { code: 'tl', name: 'Timor-Leste', dialCode: '+670', country_code_id_1: 52 },
            { code: 'tm', name: 'Turkmenistan', dialCode: '+993', country_code_id_1: 178 },
            { code: 'tn', name: 'Tunisia', dialCode: '+216', country_code_id_1: 176 },
            { code: 'tr', name: 'Turkey', dialCode: '+90', country_code_id_1: 177 },
            { code: 'tw', name: 'Taiwan', dialCode: '+886', country_code_id_1: 171 },
            { code: 'tz', name: 'Tanzania', dialCode: '+255', country_code_id_1: 173 },
            { code: 'ua', name: 'Ukraine', dialCode: '+380', country_code_id_1: 180 },
            { code: 'ug', name: 'Uganda', dialCode: '+256', country_code_id_1: 179 },
            { code: 'us', name: 'United States', dialCode: '+1', country_code_id_1: 183 },
            { code: 'uy', name: 'Uruguay', dialCode: '+598', country_code_id_1: 184 },
            { code: 'uz', name: 'Uzbekistan', dialCode: '+998', country_code_id_1: 185 },
            { code: 'vu', name: 'Vanuatu', dialCode: '+678', country_code_id_1: 186 },
            { code: 've', name: 'Venezuela', dialCode: '+58', country_code_id_1: 187 },
            { code: 'vn', name: 'Vietnam', dialCode: '+84', country_code_id_1: 188 },
            { code: 'ws', name: 'Samoa', dialCode: '+685', country_code_id_1: 150 },
            { code: 'ye', name: 'Yemen', dialCode: '+967', country_code_id_1: 189 },
            { code: 'za', name: 'South Africa', dialCode: '+27', country_code_id_1: 161 },
            { code: 'zm', name: 'Zambia', dialCode: '+260', country_code_id_1: 190 },
            { code: 'zw', name: 'Zimbabwe', dialCode: '+263', country_code_id_1: 191 },
            { code: 'xk', name: 'Kosovo', dialCode: '+383', country_code_id_1: 95 }
        ];
    }
    
    init() {
        this.createWrapper();
        this.createCountrySelector();
        this.setupInput();
        this.setupEventListeners();
        this.updateInputValue();
    }
    
    createWrapper() {
        // Create wrapper div
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'custom-phone-input';
        
        // Insert wrapper before input
        this.input.parentNode.insertBefore(this.wrapper, this.input);
        
        // Move input inside wrapper
        this.wrapper.appendChild(this.input);
    }
    
    createCountrySelector() {
        // Create country selector button
        this.selectorButton = document.createElement('button');
        this.selectorButton.type = 'button';
        this.selectorButton.className = 'custom-phone-input__selector';
        
        // Show placeholder when no country is selected
        if (this.selectedCountry) {
            this.selectorButton.innerHTML = `
                <span class="custom-phone-input__flag fi fi-${this.selectedCountry.code}"></span>
                <span class="custom-phone-input__arrow"></span>
            `;
        } else {
            this.selectorButton.innerHTML = `
                <span class="custom-phone-input__flag-placeholder">🌍</span>
                <span class="custom-phone-input__arrow"></span>
            `;
        }
        
        // Create dropdown
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'custom-phone-input__dropdown';
        this.dropdown.style.display = 'none';
        
        // Create search input
        this.searchInput = document.createElement('input');
        this.searchInput.type = 'text';
        this.searchInput.className = 'custom-phone-input__search';
        this.searchInput.placeholder = 'Search countries...';
        
        // Create countries container
        this.countriesContainer = document.createElement('div');
        this.countriesContainer.className = 'custom-phone-input__countries';
        
        // Add search and countries to dropdown
        this.dropdown.appendChild(this.searchInput);
        this.dropdown.appendChild(this.countriesContainer);
        
        // Populate dropdown
        this.populateDropdown();
        
        // Insert before input
        this.wrapper.insertBefore(this.selectorButton, this.input);
        this.wrapper.appendChild(this.dropdown);
    }
    
    populateDropdown() {
        // Store all countries for filtering
        this.allCountries = this.getSortedCountries();
        this.renderCountries(this.allCountries);
    }
    
    getSortedCountries() {
        // Sort countries: preferred first, then alphabetically
        const preferredCountries = this.countries.filter(c => 
            this.options.preferredCountries.includes(c.code)
        );
        const otherCountries = this.countries.filter(c => 
            !this.options.preferredCountries.includes(c.code)
        ).sort((a, b) => a.name.localeCompare(b.name));
        
        return [...preferredCountries, ...otherCountries];
    }
    
    renderCountries(countries) {
        // Clear existing countries
        this.countriesContainer.innerHTML = '';
        
        const preferredCount = this.options.preferredCountries.length;
        
        countries.forEach((country, index) => {
            const option = document.createElement('div');
            option.className = 'custom-phone-input__option';
            option.dataset.countryCode = country.code;
            option.dataset.countryName = country.name.toLowerCase();
            option.dataset.dialCode = country.dialCode;
            
            // Add separator after preferred countries (only if we're showing all countries)
            if (index === preferredCount && preferredCount > 0 && countries.length > preferredCount) {
                const separator = document.createElement('div');
                separator.className = 'custom-phone-input__separator';
                this.countriesContainer.appendChild(separator);
            }
            
            option.innerHTML = `
                <span class="custom-phone-input__flag fi fi-${country.code}"></span>
                <span class="custom-phone-input__country-name">${country.name}</span>
                <span class="custom-phone-input__dial-code">${country.dialCode}</span>
            `;
            
            option.addEventListener('click', () => {
                this.selectCountry(country);
                this.closeDropdown();
            });
            
            this.countriesContainer.appendChild(option);
        });
        
        // Show "No results" message if no countries match
        if (countries.length === 0) {
            const noResults = document.createElement('div');
            noResults.className = 'custom-phone-input__no-results';
            noResults.textContent = 'No countries found';
            this.countriesContainer.appendChild(noResults);
        }
    }
    
    setupInput() {
        // Add custom class to input
        this.input.classList.add('custom-phone-input__field');
        
        // Set initial placeholder
        this.updatePlaceholder();
    }
    
    setupEventListeners() {
        // Toggle dropdown
        this.selectorButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleDropdown();
        });
        
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.wrapper.contains(e.target)) {
                this.closeDropdown();
            }
        });
        
        // Handle input changes
        this.input.addEventListener('input', () => {
            this.formatPhoneNumber();
        });
        
        // Handle search input
        this.searchInput.addEventListener('input', (e) => {
            this.filterCountries(e.target.value);
        });
        
        // Handle search input keyboard events
        this.searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.focusFirstCountry();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.closeDropdown();
            }
        });
        
        // Handle keyboard navigation in dropdown
        this.dropdown.addEventListener('keydown', (e) => {
            this.handleKeyboardNavigation(e);
        });
        
        // Prevent search input from closing dropdown when clicked
        this.searchInput.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }
    
    selectCountry(country) {
        this.selectedCountry = country;
        
        // Update selector button to show selected flag
        this.selectorButton.innerHTML = `
            <span class="custom-phone-input__flag fi fi-${country.code}"></span>
            <span class="custom-phone-input__arrow"></span>
        `;
        
        // Update input
        this.updateInputValue();
        this.updatePlaceholder();
        
        // Trigger change event with detail about whether it's programmatic
        const event = new CustomEvent('countrychange', { 
            bubbles: true,
            detail: { 
                isProgrammatic: this.isSettingCountryProgrammatically 
            }
        });
        this.input.dispatchEvent(event);
    }
    
    updateInputValue() {
        // Only update input if a country is selected
        if (!this.selectedCountry || !this.selectedCountry.dialCode) {
            this.input.value = '';
            return;
        }
        
        const currentValue = this.input.value;
        
        // If input is empty or only contains country code, set the country code
        if (!currentValue || currentValue === this.getPreviousDialCode()) {
            this.input.value = this.selectedCountry.dialCode + ' ';
        } else {
            // Replace old country code with new one
            const phoneNumber = this.extractPhoneNumber(currentValue);
            this.input.value = this.selectedCountry.dialCode + ' ' + phoneNumber;
        }
        
        // Position cursor after country code
        setTimeout(() => {
            if (this.selectedCountry && this.selectedCountry.dialCode) {
                const dialCodeLength = this.selectedCountry.dialCode.length + 1; // +1 for space
                this.input.setSelectionRange(dialCodeLength, dialCodeLength);
            }
        }, 0);
    }
    
    updatePlaceholder() {
        // Set placeholder based on selected country
        if (this.selectedCountry && this.selectedCountry.dialCode) {
            // Set placeholder to show example number format
            this.input.placeholder = `${this.selectedCountry.dialCode} 123 456 789`;
        } else {
            this.input.placeholder = 'Select country first';
        }
    }
    
    extractPhoneNumber(fullValue) {
        // Remove any country code from the beginning
        const cleaned = fullValue.replace(/^\+\d+\s*/, '').trim();
        return cleaned;
    }
    
    getPreviousDialCode() {
        // This would need to track the previous country's dial code
        // For now, return empty string
        return '';
    }
    
    formatPhoneNumber() {
        const value = this.input.value;
        
        // Auto-detect country from phone number if it starts with + (but not during programmatic changes)
        if (value.trim().startsWith('+') && !this.isSettingCountryProgrammatically) {
            this.autoDetectCountry(value);
        }
        
        // Only format if a country is selected
        if (!this.selectedCountry || !this.selectedCountry.dialCode) {
            return;
        }
        
        // Ensure country code is always present
        if (!value.startsWith(this.selectedCountry.dialCode)) {
            const phoneNumber = this.extractPhoneNumber(value);
            this.input.value = this.selectedCountry.dialCode + ' ' + phoneNumber;
        }
        
        // Basic formatting (can be enhanced based on country-specific rules)
        let formatted = this.input.value;
        
        // Remove extra spaces
        formatted = formatted.replace(/\s+/g, ' ');
        
        // Ensure single space after country code
        const dialCodePattern = new RegExp(`^(\\${this.selectedCountry.dialCode})\\s*`);
        formatted = formatted.replace(dialCodePattern, `$1 `);
        
        this.input.value = formatted;
    }
    
    autoDetectCountry(phoneNumber) {
        // Extract the dial code from the phone number
        const cleaned = phoneNumber.trim();
        
        // Try to match against all country dial codes
        // Sort by length (longest first) to match +1268 before +1
        const sortedCountries = [...this.countries].sort((a, b) => 
            b.dialCode.length - a.dialCode.length
        );
        
        for (const country of sortedCountries) {
            if (cleaned.startsWith(country.dialCode)) {
                // Only change country if it's different from current
                if (!this.selectedCountry || this.selectedCountry.code !== country.code) {
                    this.selectCountry(country);
                }
                return;
            }
        }
    }
    
    toggleDropdown() {
        if (this.dropdown.style.display === 'none') {
            this.openDropdown();
        } else {
            this.closeDropdown();
        }
    }
    
    openDropdown() {
        this.dropdown.style.display = 'block';
        this.selectorButton.classList.add('custom-phone-input__selector--open');
        
        // Clear search and show all countries
        this.searchInput.value = '';
        this.renderCountries(this.allCountries);
        
        // Focus search input
        setTimeout(() => {
            this.searchInput.focus();
        }, 10);
        
        // Highlight current country (if one is selected)
        if (this.selectedCountry) {
            const currentOption = this.countriesContainer.querySelector(`[data-country-code="${this.selectedCountry.code}"]`);
            if (currentOption) {
                currentOption.classList.add('custom-phone-input__option--selected');
                currentOption.scrollIntoView({ block: 'nearest' });
            }
        }
    }
    
    closeDropdown() {
        this.dropdown.style.display = 'none';
        this.selectorButton.classList.remove('custom-phone-input__selector--open');
        
        // Remove selection highlight
        const selectedOption = this.countriesContainer.querySelector('.custom-phone-input__option--selected');
        if (selectedOption) {
            selectedOption.classList.remove('custom-phone-input__option--selected');
        }
        
        // Clear search
        this.searchInput.value = '';
    }
    
    filterCountries(searchTerm) {
        const term = searchTerm.toLowerCase().trim();
        
        if (!term) {
            // Show all countries if search is empty
            this.renderCountries(this.allCountries);
            return;
        }
        
        // Filter countries by name or dial code
        const filteredCountries = this.allCountries.filter(country => {
            return country.name.toLowerCase().includes(term) || 
                   country.dialCode.includes(term) ||
                   country.code.toLowerCase().includes(term);
        });
        
        this.renderCountries(filteredCountries);
    }
    
    focusFirstCountry() {
        const firstOption = this.countriesContainer.querySelector('.custom-phone-input__option');
        if (firstOption) {
            // Remove existing selection
            const currentSelected = this.countriesContainer.querySelector('.custom-phone-input__option--selected');
            if (currentSelected) {
                currentSelected.classList.remove('custom-phone-input__option--selected');
            }
            
            // Select first option
            firstOption.classList.add('custom-phone-input__option--selected');
            firstOption.scrollIntoView({ block: 'nearest' });
        }
    }
    
    handleKeyboardNavigation(e) {
        // Add keyboard navigation support
        const options = this.countriesContainer.querySelectorAll('.custom-phone-input__option');
        const currentSelected = this.countriesContainer.querySelector('.custom-phone-input__option--selected');
        let currentIndex = Array.from(options).indexOf(currentSelected);
        
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                currentIndex = Math.min(currentIndex + 1, options.length - 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                currentIndex = Math.max(currentIndex - 1, 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (currentSelected) {
                    currentSelected.click();
                }
                return;
            case 'Escape':
                e.preventDefault();
                this.closeDropdown();
                return;
        }
        
        // Update selection
        if (currentSelected) {
            currentSelected.classList.remove('custom-phone-input__option--selected');
        }
        if (options[currentIndex]) {
            options[currentIndex].classList.add('custom-phone-input__option--selected');
            options[currentIndex].scrollIntoView({ block: 'nearest' });
        }
    }
    
    // Public methods
    getSelectedCountry() {
        return this.selectedCountry;
    }
    
    getCountryCodeId() {
        return this.selectedCountry ? this.selectedCountry.country_code_id_1 : null;
    }
    
    getPhoneNumber() {
        return this.input.value;
    }
    
    getFormattedNumber() {
        return this.input.value.replace(/\s+/g, '');
    }
    
    getPhoneNumberWithoutDialCode() {
        const phoneNumber = this.input.value.trim();
        
        if (!this.selectedCountry || !phoneNumber) {
            return phoneNumber;
        }
        
        // Remove the dial code from the phone number
        const dialCode = this.selectedCountry.dialCode;
        
        // Try multiple patterns to remove dial code
        let cleanNumber = phoneNumber;
        
        // Pattern 1: Remove dial code with flexible spacing (e.g., "+371 28366610" or "+371  28366610")
        const dialCodePattern = new RegExp(`^\\${dialCode.replace(/\s/g, '\\s*')}`);
        cleanNumber = cleanNumber.replace(dialCodePattern, '').trim();
        
        // Pattern 2: Remove just the numeric part with optional + and spaces (e.g., "+371 " or "371 ")
        const numericDialCode = dialCode.replace(/\D/g, '');
        const numericPattern = new RegExp(`^\\+?${numericDialCode}\\s*`);
        cleanNumber = cleanNumber.replace(numericPattern, '').trim();
        
        // Pattern 3: Remove any remaining leading + or spaces
        cleanNumber = cleanNumber.replace(/^\+\s*/, '').trim();
        
        return cleanNumber;
    }
    
    setCountry(countryCode) {
        const country = this.countries.find(c => c.code === countryCode);
        if (country) {
            // Set flag to prevent auto-detection during programmatic country change
            this.isSettingCountryProgrammatically = true;
            this.selectCountry(country);
            // Reset flag after a short delay to allow formatting to complete
            setTimeout(() => {
                this.isSettingCountryProgrammatically = false;
            }, 100);
        }
    }
    
    destroy() {
        // Clean up event listeners and DOM
        if (this.wrapper && this.wrapper.parentNode) {
            this.wrapper.parentNode.insertBefore(this.input, this.wrapper);
            this.wrapper.parentNode.removeChild(this.wrapper);
        }
    }
}

// Export for use in other files
window.CustomPhoneInput = CustomPhoneInput;
